import type { UserSavedSearch } from "@/modules/jobAds/client/api/types"
import type { MultipleQueriesQuery } from "@algolia/autocomplete-preset-algolia"

import { AND_OPERATOR, NOT_OPERATOR } from "@/modules/jobAds/client/constants"
import { formatFiltersForAlgolia } from "@/modules/jobAds/client/utils/formatFiltersForAlgolia"
import { getAlgoliaFacetFiltersFromBusinessRules } from "@/modules/jobAds/client/utils/getAlgoliaFacetFiltersFromBusinessRules"
import {
  type AlgoliaJobAdsRequestParameters,
  getAlgoliaFacetFiltersFromParams,
} from "@/modules/jobAds/client/utils/getAlgoliaFacetFiltersFromParams"
import { getAlgoliaLatLngParams } from "@/modules/jobAds/client/utils/getAlgoliaLatLngParams"
import { getJobAdsAvailableFiltersFromSearchParams } from "@/modules/jobAds/client/utils/getJobAdsAvailableFiltersFromSearchParams"
import { getJobAdsSearchParamsFromURLSearchParams } from "@/modules/jobAds/client/utils/getJobAdsSearchParamsFromURLSearchParams"
import { computeLocationObjectFromSingleQueryParam, getLocationType } from "@/modules/locations/locations"
import { parsedUrlQueryToURLSearchParams } from "@/modules/routing/helpers/parsedUrlQueryToURLSearchParams"

type ComputeSavedSearchQueryOptions = {
  indexName?: string
  initialParams?: AlgoliaJobAdsRequestParameters
  userCountry?: string | null
  seenJobAds?: string[]
}

export const computeSavedSearchQuery = (
  savedSearch: UserSavedSearch,
  options: ComputeSavedSearchQueryOptions
): MultipleQueriesQuery => {
  const {
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_INDEX_NAME,
    initialParams = {} as AlgoliaJobAdsRequestParameters,
    userCountry,
    seenJobAds = [],
  } = options

  const { lastSearchTime, searchParams } = savedSearch
  const savedSearchAvailableFilters = getJobAdsAvailableFiltersFromSearchParams(searchParams)
  const savedURLSearchParams = parsedUrlQueryToURLSearchParams(savedSearchAvailableFilters, new URLSearchParams({}))

  const savedSearchParams = getJobAdsSearchParamsFromURLSearchParams(
    savedURLSearchParams
  ) as unknown as AlgoliaJobAdsRequestParameters

  const mergedSearchParams = {
    ...initialParams,
    ...savedSearchParams,
  }

  const savedSearchFacetFilters = getAlgoliaFacetFiltersFromBusinessRules(
    getAlgoliaFacetFiltersFromParams(mergedSearchParams, userCountry),
    initialParams
  )

  const savedSearchLocationObject = savedSearchParams.location
    ? computeLocationObjectFromSingleQueryParam(savedSearchParams.location)
    : null
  const savedSearchLocationType = savedSearchLocationObject ? getLocationType(savedSearchLocationObject) : null

  const savedSearchAroundLatLngParams = getAlgoliaLatLngParams(
    savedSearchParams.lat,
    savedSearchParams.lng,
    savedSearchParams.radius
  )

  const lastSearchTimeTimestamp = Math.floor(Date.parse(lastSearchTime) / 1000)

  const filteredByUnseenJobAds = seenJobAds?.length
    ? `${AND_OPERATOR} ${seenJobAds?.map(jobAdId => `${NOT_OPERATOR} objectID:${jobAdId}`).join(` ${AND_OPERATOR} `)}`
    : ""
  const filteredByActiveDate = `${AND_OPERATOR} activated_at > ${lastSearchTimeTimestamp}`

  return {
    indexName,
    params: {
      attributesToRetrieve: [],
      filters: `${formatFiltersForAlgolia(savedSearchFacetFilters)} ${filteredByUnseenJobAds} ${filteredByActiveDate}`,
      ...(savedSearchLocationType === "city" && { ...savedSearchAroundLatLngParams }),
      hitsPerPage: 0,
    },
    query: savedSearchParams.userQuery,
  }
}
